import React, { useState } from 'react';
import { useData } from '../../store/DataContext';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { MailIcon, MapPinIcon, PhoneIcon, PlusIcon, TruckIcon } from 'lucide-react';
type SupplierForm = {
  name: string;
  contactPerson: string;
  phone: string;
  email: string;
  address: string;
};
const initialForm: SupplierForm = {
  name: '',
  contactPerson: '',
  phone: '',
  email: '',
  address: ''
};
export function Suppliers() {
  const {
    suppliers,
    addSupplier,
    updateSupplier
  } = useData();
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<SupplierForm>(initialForm);
  const [error, setError] = useState('');
  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!form.name.trim()) {
      setError('Supplier name is required.');
      return;
    }
    const contactDetails = {
      ...(form.contactPerson.trim() ? {
        contactPerson: form.contactPerson.trim()
      } : {}),
      ...(form.phone.trim() ? {
        phone: form.phone.trim()
      } : {}),
      ...(form.email.trim() ? {
        email: form.email.trim()
      } : {}),
      ...(form.address.trim() ? {
        address: form.address.trim()
      } : {})
    };
    addSupplier({
      name: form.name.trim(),
      ...contactDetails,
      status: 'Active'
    });
    setForm(initialForm);
    setError('');
    setShowForm(false);
  };
  const toggleSupplierStatus = (supplierId: string, status: 'Active' | 'Inactive') => {
    updateSupplier(supplierId, {
      status: status === 'Active' ? 'Inactive' : 'Active'
    });
  };
  return <div className="space-y-5" data-id="element-5331">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between" data-id="element-5332">
        <div data-id="element-5333">
          <h2 className="text-xl font-bold text-slate-950" data-id="element-5334">Suppliers</h2>
          <p className="mt-1 text-sm text-slate-500" data-id="element-5335">
            Supplier name is required. Contact details are optional and remain
            available on each linked delivery batch.
          </p>
        </div>
        <Button onClick={() => setShowForm(true)} className="bg-teal-600 hover:bg-teal-700" data-id="element-5336">
          <PlusIcon className="mr-2 h-4 w-4" data-id="element-5337" /> Add supplier
        </Button>
      </div>
      {showForm && <form onSubmit={submit} className="grid grid-cols-1 gap-4 rounded-xl border border-teal-200 bg-teal-50/50 p-5 md:grid-cols-2" data-id="element-5338">
          <div className="flex justify-between md:col-span-2" data-id="element-5339">
            <div data-id="element-5340">
              <h3 className="font-bold text-slate-900" data-id="element-5341">New supplier</h3>
              <p className="mt-1 text-xs text-slate-500" data-id="element-5342">
                Only the supplier name is mandatory.
              </p>
            </div>
            <button type="button" onClick={() => setShowForm(false)} className="text-sm font-semibold text-slate-500 hover:text-slate-900" data-id="element-5343">
              Cancel
            </button>
          </div>
          {error && <p role="alert" className="text-sm text-red-600 md:col-span-2" data-id="element-5344">
              {error}
            </p>}
          <Input label="Supplier name" required value={form.name} onChange={event => setForm({
        ...form,
        name: event.target.value
      })} data-id="element-5345" />
          <Input label="Contact person (optional)" value={form.contactPerson} onChange={event => setForm({
        ...form,
        contactPerson: event.target.value
      })} data-id="element-5346" />
          <Input label="Phone (optional)" type="tel" value={form.phone} onChange={event => setForm({
        ...form,
        phone: event.target.value
      })} data-id="element-5347" />
          <Input label="Email (optional)" type="email" value={form.email} onChange={event => setForm({
        ...form,
        email: event.target.value
      })} data-id="element-5348" />
          <Input label="Address (optional)" className="md:col-span-2" value={form.address} onChange={event => setForm({
        ...form,
        address: event.target.value
      })} data-id="element-5349" />
          <div className="flex justify-end md:col-span-2" data-id="element-5350">
            <Button type="submit" className="bg-teal-600 hover:bg-teal-700" data-id="element-5351">
              Save supplier
            </Button>
          </div>
        </form>}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2" data-id="element-5352">
        {suppliers.map(supplier => <article key={supplier.id} className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm" data-id="element-5353">
            <div className="flex items-start gap-3" data-id="element-5354">
              <div className="rounded-lg bg-teal-50 p-2.5 text-teal-700" data-id="element-5355">
                <TruckIcon className="h-5 w-5" data-id="element-5356" />
              </div>
              <div className="min-w-0 flex-1" data-id="element-5357">
                <div className="flex flex-wrap items-center justify-between gap-2" data-id="element-5358">
                  <h3 className="font-bold text-slate-900" data-id="element-5359">{supplier.name}</h3>
                  <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${supplier.status === 'Active' ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-600'}`} data-id="element-5360">
                    {supplier.status}
                  </span>
                </div>
                {supplier.contactPerson && <p className="mt-2 text-sm font-medium text-slate-700" data-id="element-5361">
                    {supplier.contactPerson}
                  </p>}
                <div className="mt-2 space-y-1 text-sm text-slate-500" data-id="element-5362">
                  {supplier.phone && <p className="flex items-center gap-2" data-id="element-5363">
                      <PhoneIcon className="h-3.5 w-3.5" data-id="element-5364" /> {supplier.phone}
                    </p>}
                  {supplier.email && <p className="flex items-center gap-2" data-id="element-5365">
                      <MailIcon className="h-3.5 w-3.5" data-id="element-5366" /> {supplier.email}
                    </p>}
                  {supplier.address && <p className="flex items-start gap-2" data-id="element-5367">
                      <MapPinIcon className="mt-0.5 h-3.5 w-3.5 shrink-0" data-id="element-5368" />
                      {supplier.address}
                    </p>}
                  {!supplier.contactPerson && !supplier.phone && !supplier.email && !supplier.address && <p data-id="element-5369">No contact details recorded.</p>}
                </div>
                <Button type="button" size="sm" variant="outline" onClick={() => toggleSupplierStatus(supplier.id, supplier.status)} className="mt-4" data-id="element-5370">
                  {supplier.status === 'Active' ? 'Deactivate' : 'Activate'}
                </Button>
              </div>
            </div>
          </article>)}
        {!suppliers.length && <div className="col-span-full rounded-xl border border-dashed border-slate-300 py-14 text-center text-sm text-slate-500" data-id="element-5371">
            No suppliers yet. Add a supplier before receiving a delivery or
            creating a purchase order.
          </div>}
      </div>
    </div>;
}